import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CloseIcon from '@mui/icons-material/Close'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Tooltip } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import KarteraAdminTemplate from '../../template/KarteraAdminTemplate'
import APIInvoke from '../../utils/APIInvoke'
import { RamoMark } from '../customers/common/customer-common'
import PolicyClose from '../policies/close/policy-close'
import { statusFromService, vigenciaView } from '../policies/common/policy-common'
import PolicyDrawer from '../policies/drawer/policy-drawer'
import CommunicationDrawer from './CommunicationDrawer'
import RenewalDrawer from './RenewalDrawer'
import {
    AGING,
    DUE_RANGES,
    buildCsv,
    daysFromToday,
    defaultWorkflowStatus,
    downloadCsv,
    dueDaysLabel,
    dueRangeOf,
    endDateOf,
    formatCount,
    insuredName,
    isClosedStatus,
    money,
    moneyCompact,
    primaOf,
    ramoOf,
    shiftIso,
    startDateOf,
} from './renewalFormat'

const POLIZAS_QUERY = 'size=500&status=Vigente&polizaIDRenovo[nu]&order=fechaVigenciaA ASC'

const toRenewalRow = (item) => ({
    ...item,
    fechaVigenciaDe: startDateOf(item),
    fechaVigenciaA: endDateOf(item),
    days: daysFromToday(endDateOf(item)),
})

const Renewals = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [initialLoadDone, setInitialLoadDone] = useState(false)
    const [error, setError] = useState(null)
    const [polizas, setPolizas] = useState([])
    const [balances, setBalances] = useState([])
    const [totalItems, setTotalItems] = useState(0)
    const [reloadToken, setReloadToken] = useState(0)
    const [rangeId, setRangeId] = useState(DUE_RANGES[0].id)
    const [workflow, setWorkflow] = useState({})
    const [selectedId, setSelectedId] = useState(null)
    const [detailId, setDetailId] = useState(null)
    const [remind, setRemind] = useState(null)
    const [closeId, setCloseId] = useState(null)
    const [toast, setToast] = useState(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const range = DUE_RANGES.find((item) => item.id === rangeId) || DUE_RANGES[0]
                const response = await APIInvoke.invokeGET(`/polizas?fechaVigenciaA[bw]=${shiftIso(range.from)},${shiftIso(range.to)}&${POLIZAS_QUERY}`)
                const payload = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || 'No se pudo cargar las renovaciones.')
                const items = (payload.body?.items || []).map(toRenewalRow)
                setPolizas(items)
                setTotalItems(payload.body?.totalItems || items.length)
                setInitialLoadDone(true)
            } catch (loadError) {
                if (active) setError(loadError.message || 'No se pudo cargar las renovaciones.')
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [reloadToken, rangeId])

    useEffect(() => {
        let active = true
        const loadBalances = async () => {
            try {
                const todos = DUE_RANGES.find((item) => item.id === 'todos') || DUE_RANGES[0]
                const response = await APIInvoke.invokeGET(`/polizas?fechaVigenciaA[bw]=${shiftIso(todos.from)},${shiftIso(todos.to)}&${POLIZAS_QUERY}`)
                const payload = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || 'No se pudo cargar el fin de vigencia.')
                setBalances((payload.body?.items || []).map(toRenewalRow))
            } catch {
                if (active) setBalances([])
            }
        }
        loadBalances()
        return () => { active = false }
    }, [reloadToken])

    const selectRange = (id) => {
        setRangeId(id)
        setReloadToken((token) => token + 1)
    }

    useEffect(() => {
        if (!toast) return undefined
        const timer = window.setTimeout(() => setToast(null), 4000)
        return () => window.clearTimeout(timer)
    }, [toast])

    const enriched = useMemo(() => polizas.map((poliza) => {
        const local = workflow[poliza.polizaId] || {}
        const status = local.status || defaultWorkflowStatus(poliza)
        return {
            ...poliza,
            workflow: {
                status,
                renovPrima: local.renovPrima ?? null,
                renovCia: local.renovCia || poliza.aseguradora || '',
                cambios: local.cambios || '',
            },
        }
    }), [polizas, workflow])

    const openRenewals = useMemo(() => balances.filter((poliza) => {
        const local = workflow[poliza.polizaId] || {}
        return !isClosedStatus(local.status || defaultWorkflowStatus(poliza))
    }), [balances, workflow])

    const horizonte = useMemo(() => AGING.map((bucket) => {
        const items = openRenewals.filter((item) => item.days != null && bucket.test(item.days))
        const amount = items.reduce((sum, item) => sum + primaOf(item), 0)
        return { ...bucket, items, amount }
    }), [openRenewals])

    const maxCount = Math.max(...horizonte.map((bucket) => bucket.items.length), 0)
    const totalPrima = openRenewals.reduce((sum, item) => sum + primaOf(item), 0)

    const visible = [...enriched].sort((a, b) => (a.days ?? 9999) - (b.days ?? 9999))

    const selected = enriched.find((item) => item.polizaId === selectedId) || null
    const aseguradoras = useMemo(
        () => Array.from(new Set(polizas.map((item) => item.aseguradora).filter(Boolean))),
        [polizas],
    )

    const patchWorkflow = (polizaId, patch) => {
        setWorkflow((current) => ({
            ...current,
            [polizaId]: { ...(current[polizaId] || {}), ...patch },
        }))
        if (patch.renovPrima != null) setToast({ message: 'Cotización de renovación guardada' })
        if (patch.status === 'renovada') {
            const item = polizas.find((row) => row.polizaId === polizaId)
            setToast({ message: `${item?.noPoliza || 'Póliza'} marcada como renovada` })
        }
        if (patch.status === 'norenueva') {
            const item = polizas.find((row) => row.polizaId === polizaId)
            setToast({ message: `${item?.noPoliza || 'Póliza'} marcada como «No renueva»` })
        }
    }

    const exportRows = () => {
        const rows = visible.map((item) => {
            const status = statusFromService(item.status, item.fechaVigenciaA)
            const vigencia = vigenciaView(item.fechaVigenciaDe, item.fechaVigenciaA, item.status)
            return {
                noPoliza: item.noPoliza || '',
                name: insuredName(item),
                aseguradora: item.aseguradora || '',
                fin: vigencia.label,
                prima: money(primaOf(item), item.moneda),
                statusLabel: status.label,
                days: item.days ?? '',
            }
        })
        downloadCsv(`renovaciones-${new Date().toISOString().slice(0, 10)}.csv`, buildCsv(rows))
        setToast({ message: `Exportadas ${formatCount(rows.length)} pólizas` })
    }

    return (
        <KarteraAdminTemplate loading={false} padded={false} title="Renovaciones">
            <PolicyDrawer polizaId={detailId} onClose={() => setDetailId(null)} backUrl="/renovaciones" />
            <RenewalDrawer
                poliza={selected}
                aseguradoras={aseguradoras}
                onClose={() => setSelectedId(null)}
                onPatch={patchWorkflow}
                onRemind={(item) => setRemind({ poliza: item })}
            />
            <CommunicationDrawer
                entidad="poliza"
                target={remind}
                onClose={() => setRemind(null)}
                onDone={(message) => {
                    setRemind(null)
                    setToast({ message })
                }}
            />
            <PolicyClose
                polizaId={closeId}
                onClose={() => setCloseId(null)}
                onCompleted={(poliza) => {
                    setCloseId(null)
                    if (poliza?.polizaId || poliza?.id) {
                        const id = poliza.polizaId || poliza.id
                        setPolizas((items) => items.filter((item) => item.polizaId !== id))
                        setWorkflow((current) => ({
                            ...current,
                            [id]: { ...(current[id] || {}), status: 'norenueva' },
                        }))
                    }
                    setToast({ message: `Póliza ${poliza?.noPoliza || ''} cerrada` })
                    setReloadToken((token) => token + 1)
                }}
            />

            <div className="flex min-h-0 flex-1 flex-col gap-5 bg-k-bg px-[clamp(16px,2.5vw,32px)] pb-10 pt-6">
                {loading && !initialLoadDone && (
                    <div className="flex flex-col gap-5">
                        <div className="h-8 w-56 animate-pulse rounded bg-k-line" />
                        <div className="h-40 animate-pulse rounded-[18px] bg-k-line" />
                        <div className="h-80 animate-pulse rounded-[18px] bg-k-line" />
                    </div>
                )}

                {error && !loading && (
                    <div className="rounded-lg border border-k-danger/30 bg-k-danger-soft p-4">
                        <div className="flex items-center">
                            <ErrorOutlineIcon className="mr-2 text-k-danger" style={{ fontSize: 20 }} />
                            <p className="m-0 text-sm text-k-danger-ink">{error}</p>
                        </div>
                        <button type="button" className="mt-3 h-10 rounded-xl bg-k-danger px-4 text-sm font-semibold text-white" onClick={() => setReloadToken((token) => token + 1)}>
                            Reintentar
                        </button>
                    </div>
                )}

                {initialLoadDone && !error && (
                    <>
                        <header className="flex flex-wrap items-end justify-between gap-4">
                            <div className="flex flex-col gap-1">
                                <span className="text-[13px] font-medium text-k-muted">
                                    {formatCount(openRenewals.length)} pólizas terminan su vigencia entre −60 y 60 días
                                    {openRenewals.length > 0 ? ` · ${moneyCompact(totalPrima)} de prima a renovar` : ''}
                                </span>
                                <h1 className="m-0 text-[28px] font-extrabold tracking-[-0.03em] text-k-ink">Renovaciones</h1>
                            </div>
                            <div className="flex flex-wrap gap-2.5">
                                <button
                                    type="button"
                                    aria-label="Actualizar"
                                    title="Actualizar"
                                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-k-line bg-k-surface text-k-ink hover:bg-k-hover"
                                    onClick={() => setReloadToken((token) => token + 1)}
                                >
                                    <RefreshIcon className={loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                                </button>
                                <button
                                    type="button"
                                    className="h-11 rounded-xl border border-k-line-strong bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                                    onClick={exportRows}
                                    disabled={visible.length === 0}
                                >
                                    Exportar
                                </button>
                            </div>
                        </header>

                        <section className="flex flex-col gap-[18px] rounded-[18px] border border-k-line bg-k-surface p-[22px]">
                            <div className="flex flex-wrap items-baseline justify-between gap-3">
                                <h2 className="m-0 text-[17px] font-semibold text-k-ink">Fin de vigencia</h2>
                                <span className="text-[13px] text-k-muted">Pólizas de −60 a 60 días del fin de su vigencia</span>
                            </div>
                            <div className="flex h-[18px] gap-0.5 overflow-hidden rounded-[9px]" role="img" aria-label="Distribución de pólizas por días para el fin de vigencia">
                                {horizonte.map((bucket) => (
                                    <button
                                        key={bucket.id}
                                        type="button"
                                        title={bucket.label}
                                        aria-label={bucket.label}
                                        className="h-full border-0 p-0"
                                        style={{
                                            flex: `${maxCount ? bucket.items.length : 1} 1 0`,
                                            background: bucket.color,
                                            minWidth: bucket.items.length ? 4 : 0,
                                        }}
                                        onClick={() => selectRange(bucket.id)}
                                    />
                                ))}
                            </div>
                            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4">
                                {horizonte.map((bucket) => {
                                    const on = rangeId === bucket.id
                                    return (
                                        <button
                                            key={bucket.id}
                                            type="button"
                                            aria-pressed={on}
                                            className={`flex flex-col gap-1.5 rounded-2xl border bg-transparent p-3 text-left ${on ? 'border-k-ink' : 'border-transparent hover:bg-k-hover'}`}
                                            onClick={() => selectRange(bucket.id)}
                                        >
                                            <span className="flex items-center gap-2 text-[13px] text-k-muted">
                                                <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: bucket.color }} />
                                                {bucket.label}
                                            </span>
                                            <span className="text-[22px] font-semibold tabular-nums text-k-ink">{formatCount(bucket.items.length)} pólizas</span>
                                            <span className="text-xs text-k-muted">{moneyCompact(bucket.amount)} de prima</span>
                                        </button>
                                    )
                                })}
                            </div>
                        </section>

                        <section className={`flex flex-col overflow-hidden rounded-[18px] border border-k-line bg-k-surface ${loading ? 'opacity-60' : ''}`}>
                            <div className="flex flex-wrap justify-start gap-2 border-b border-k-line px-[18px] py-4" role="group" aria-label="Días para el fin de vigencia">
                                {DUE_RANGES.map((filter) => {
                                    const on = rangeId === filter.id
                                    const label = filter.label || `${filter.from} a ${filter.to} días`
                                    const tone = filter.neutral
                                        ? (on ? 'border-k-ink bg-k-ink text-k-surface' : 'border-k-line-strong bg-k-surface text-k-ink hover:bg-k-hover')
                                        : (on ? '' : 'bg-k-surface text-k-ink hover:bg-k-hover')
                                    return (
                                        <button
                                            key={filter.id}
                                            type="button"
                                            aria-pressed={on}
                                            className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-3.5 text-sm font-semibold ${tone}`}
                                            style={filter.neutral ? undefined : {
                                                background: on ? filter.color : undefined,
                                                borderColor: filter.color,
                                                color: on ? (filter.light ? '#1B1D29' : '#fff') : undefined,
                                            }}
                                            onClick={() => selectRange(filter.id)}
                                        >
                                            <span
                                                className={`h-2.5 w-2.5 rounded-full ${filter.neutral ? (on ? 'bg-k-surface' : 'bg-k-ink') : ''}`}
                                                style={filter.neutral ? undefined : { background: on ? (filter.light ? '#1B1D29' : '#fff') : filter.color }}
                                            />
                                            {label}
                                        </button>
                                    )
                                })}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[1060px] border-collapse text-sm">
                                    <thead>
                                        <tr className="bg-k-sunken text-left text-xs font-semibold uppercase tracking-[0.04em] text-k-muted">
                                            <th className="py-3 pl-[18px] pr-2.5">Póliza</th>
                                            <th className="px-2.5 py-3">Cliente</th>
                                            <th className="px-2.5 py-3">Aseguradora</th>
                                            <th className="px-2.5 py-3">Días por vencer</th>
                                            <th className="px-2.5 py-3">Vigencia</th>
                                            <th className="px-2.5 py-3 text-right">Prima actual</th>
                                            <th className="py-3 pl-2.5 pr-[18px] text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visible.length === 0 && (
                                            <tr>
                                                <td colSpan={7} className="px-4 py-12 text-center text-sm text-k-muted">No hay pólizas en este rango.</td>
                                            </tr>
                                        )}
                                        {visible.map((item) => {
                                            const name = insuredName(item)
                                            const ramo = ramoOf(item)
                                            const vigencia = vigenciaView(item.fechaVigenciaDe, item.fechaVigenciaA, item.status)
                                            const rowSelected = detailId === item.polizaId
                                            const due = dueRangeOf(item.days)
                                            return (
                                                <tr
                                                    key={item.polizaId}
                                                    className={`cursor-pointer border-t border-k-line-soft transition-colors ${rowSelected ? 'bg-k-accent-soft' : 'hover:bg-k-hover'}`}
                                                    onClick={() => setDetailId(item.polizaId)}
                                                >
                                                    <td className="py-2.5 pl-[18px] pr-2.5">
                                                        <div className="flex items-center gap-3">
                                                            <RamoMark ramo={ramo} boxed />
                                                            <div className="flex flex-col">
                                                                <span className="text-[13px] font-semibold tabular-nums text-k-ink">{item.noPoliza || '—'}</span>
                                                                <span className="text-xs text-k-muted">{ramo || 'Sin ramo'}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-2.5 py-3 font-semibold text-k-ink">{name}</td>
                                                    <td className="px-2.5 py-3">
                                                        <span className="flex min-w-0 flex-col">
                                                            <span className="truncate text-k-ink-2">{item.aseguradora || '—'}</span>
                                                            <span className="truncate text-xs tabular-nums text-k-muted">{item.agenteClave || ''}</span>
                                                        </span>
                                                    </td>
                                                    <td className="px-2.5 py-3">
                                                        <span
                                                            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-[9px] py-[3px] text-xs font-semibold ${due ? '' : 'bg-k-line text-k-ink-2'}`}
                                                            style={due ? { background: due.color, color: due.light ? '#1B1D29' : '#fff' } : undefined}
                                                        >
                                                            {due && <span className="h-1.5 w-1.5 rounded-full" style={{ background: due.light ? '#1B1D29' : '#fff' }} />}
                                                            {dueDaysLabel(item.days)}
                                                        </span>
                                                    </td>
                                                    <td className="px-2.5 py-3">
                                                        <span className="flex min-w-[150px] flex-col gap-1">
                                                            <span className="whitespace-nowrap text-[13px] text-k-ink-2">{vigencia.label}</span>
                                                            <span className="h-1 overflow-hidden rounded-sm bg-k-line" role="img" aria-label={`Avance de vigencia ${vigencia.progress}%`}>
                                                                <span className={`block h-full rounded-sm ${vigencia.barClass}`} style={{ width: `${vigencia.progress}%` }} />
                                                            </span>
                                                            {vigencia.note && <span className={`text-[11px] font-bold ${vigencia.noteClass}`}>{vigencia.note}</span>}
                                                        </span>
                                                    </td>
                                                    <td className="px-2.5 py-3 text-right text-[13px] tabular-nums text-k-ink">{money(primaOf(item), item.moneda)}</td>
                                                    <td className="py-2 pl-2.5 pr-[18px]">
                                                        <div className="flex justify-end gap-1.5">
                                                            <Tooltip title="Enviar comunicado" arrow>
                                                                <button
                                                                    type="button"
                                                                    aria-label={`Enviar aviso de renovación de ${item.noPoliza || 'la póliza'}`}
                                                                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-k-line-strong bg-k-surface text-k-ink hover:bg-k-hover"
                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation()
                                                                        setRemind({ poliza: item })
                                                                    }}
                                                                >
                                                                    <EmailOutlinedIcon style={{ fontSize: 16 }} />
                                                                </button>
                                                            </Tooltip>
                                                            <Tooltip title="Renovar póliza" arrow>
                                                                <button
                                                                    type="button"
                                                                    aria-label={`Renovar ${item.noPoliza || 'póliza'}`}
                                                                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-k-line-strong bg-k-surface text-k-ink hover:bg-k-hover"
                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation()
                                                                        navigate('/polizas/nuevo', {
                                                                            state: {
                                                                                polizaId: item.polizaId,
                                                                                action: 'renovar',
                                                                                backUrl: '/renovaciones',
                                                                            },
                                                                        })
                                                                    }}
                                                                >
                                                                    <AutorenewOutlinedIcon style={{ fontSize: 16 }} />
                                                                </button>
                                                            </Tooltip>
                                                            <Tooltip title="Cerrar póliza" arrow>
                                                                <button
                                                                    type="button"
                                                                    aria-label={`Cerrar ${item.noPoliza || 'póliza'}`}
                                                                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-k-line-strong bg-k-surface text-k-ink hover:bg-k-hover"
                                                                    onMouseDown={(event) => event.stopPropagation()}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation()
                                                                        setCloseId(item.polizaId)
                                                                    }}
                                                                >
                                                                    <CancelOutlinedIcon style={{ fontSize: 16 }} />
                                                                </button>
                                                            </Tooltip>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {totalItems > polizas.length && (
                                <p className="m-0 border-t border-k-line px-[18px] py-3 text-[13px] text-k-muted">
                                    Se muestran {formatCount(polizas.length)} de {formatCount(totalItems)} pólizas de la ventana.
                                </p>
                            )}
                        </section>
                    </>
                )}
            </div>

            {toast && (
                <div role="status" className="fixed bottom-6 right-6 z-[60] flex max-w-[calc(100%-48px)] items-center gap-3 rounded-xl bg-[#1B1D29] px-4 py-3 text-sm text-white shadow-[0_12px_32px_rgba(27,29,41,0.3)]">
                    <span>{toast.message}</span>
                    <button type="button" aria-label="Cerrar aviso" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-white" onClick={() => setToast(null)}>
                        <CloseIcon style={{ fontSize: 16 }} />
                    </button>
                </div>
            )}
        </KarteraAdminTemplate>
    )
}

export default Renewals
